import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from 'bootstrap';
import styles from './PopOver.module.css';

export enum PopOverPosition {
    bottom = 'bottom',
    top = 'top',
    left = 'left',
    right = 'right',
}

interface PopOverProps {
    title: string;
    text?: string;
    placement: string;
    trigger?: 'click' | 'hover';
    initialShow?: boolean;
    closeButton?: boolean;
    onClickCloseButton?: () => void;
    children?: string | JSX.Element | JSX.Element[];
}

const MIN_ID = 1;
const MAX_ID = 99999;

export const PopOver: FC<PopOverProps> = ({
    title,
    text,
    placement,
    trigger,
    initialShow,
    closeButton,
    onClickCloseButton,
    children,
}) => {
    // random id
    const id = useRef<string>(`${Math.floor(Math.random() * (MAX_ID - MIN_ID + 1)) + MIN_ID}`);
    const [instance, setInstance] = useState<Popover | null>(null);

    useEffect(() => {
        // destroy the popover when the component unmounts
        return () => {
            try {
                instance?.dispose();
            } catch (e) {
                console.log('catch error disposing of popover');
            }
        };
    }, [instance]);

    const onRefChange = useCallback(
        (node: HTMLSpanElement) => {
            if (node !== null) {
                const newInstance = Popover.getOrCreateInstance(node, {
                    html: true,
                    sanitize: false,
                    title:
                        title +
                        (closeButton
                            ? `<button class="btn-close ${styles['btn-close']}" onclick="document.getElementById('popover-${id.current}').click();"></button>`
                            : ''),
                    content: text || '',
                });
                initialShow === true && newInstance.show();

                setInstance(newInstance);
            }
        },
        [closeButton, initialShow, text, title]
    );

    return (
        <span
            ref={onRefChange}
            data-bs-trigger={trigger || 'hover'}
            data-bs-placement={placement}
            data-bs-custom-class={
                text !== undefined
                    ? `custom-popover ${styles['custom-popover']}`
                    : `custom-popover-title-only ${styles['custom-popover-title-only']}`
            }>
            <span id={`popover-${id.current}`} onClick={() => (onClickCloseButton ? onClickCloseButton() : instance?.hide())}>
                {children}
            </span>
        </span>
    );
};
